import { apiRequest } from "./apiClient";
import { Ticket } from "../app/views/PoMismatch/types";

type PoHelpdeskQuery = {
  ticket_no: string;
  supplier_name: string;
  summary: string | null;
  raised_at: string;
  priority: string;
  status: string;
  blocked_value: string | number | null;
  currency: string | null;
  ai_classification: string | null;
};

export type LatestPoMismatchAnalysis = {
  id: number;
  ticket_id: string;
  conversation_id: string | null;
  message_id: string | null;
  trace_id: string | null;
  input_data: unknown;
  output_data: unknown;
  processing_status: string;
  created_by: string | null;
  created_at: string;
};

const normalizePriority = (priority: string): Ticket["priority"] => {
  const normalizedPriority = priority.toLowerCase();
  if (normalizedPriority === "high") return "High";
  if (normalizedPriority === "low") return "Low";
  return "Medium";
};

const normalizeStatus = (status: string): Ticket["status"] => {
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus.includes("excluded") || normalizedStatus.includes("non-po")) return "excluded";
  if (normalizedStatus.includes("no po") || normalizedStatus.includes("no-po")) return "no-po";
  return "classified";
};

const mapQueryToTicket = (query: PoHelpdeskQuery): Ticket => {
  const blockedValue = Number(query.blocked_value || 0);
  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: query.currency || "USD",
  }).format(blockedValue);

  return {
    id: query.ticket_no,
    supplier: query.supplier_name,
    summary: query.summary || "No summary provided",
    raised: new Date(query.raised_at).toLocaleString(),
    priority: normalizePriority(query.priority),
    value: formattedValue,
    classification: query.ai_classification || query.status,
    status: normalizeStatus(query.status),
    category: "-",
  };
};

export async function fetchPoHelpdeskQueries(): Promise<Ticket[]> {
  const queries = await apiRequest("/api/po-helpdesk-queries") as PoHelpdeskQuery[];
  return queries.map(mapQueryToTicket);
}

export async function fetchLatestPoMismatchAnalysis(ticketId: string): Promise<LatestPoMismatchAnalysis | null> {
  try {
    const query = new URLSearchParams({ ticket_id: ticketId });
    return await apiRequest(`/api/po-mismatch-analysis-latest?${query.toString()}`) as LatestPoMismatchAnalysis;
  } catch (error) {
    if (error instanceof Error && error.message === "API Error: 404") return null;
    throw error;
  }
}